import { Link, navigate } from 'gatsby'
import Form from '../utils/form'
import SEO from '../utils/seo'
import React from 'react'
import tailwind from '../../tailwind.config'
import Arrow from '../assets/arrow.svg'
import Cart from '../assets/cart.svg'
import useCart from '../cart/useCart'
import Input, { RequiredAsterisk } from '../components/form/input'
import Layout from '../components/layout'
import Row from '../components/product/row'
import useFavs from '../favs/useFavs'
import useInternalLink from '../utils/useInternalLink'
import useLang from '../utils/useLang'
import useTexts from '../utils/useTexts'
import { VARIATION_SEPARATOR } from '../utils/getVariationUid'
import { getVariations } from '../components/product/dropdown'
import Spinner from '../assets/spinner.svg'

const getPrice = (product, variationId) => {
  let price = product.data.price
  if (!variationId) {
    return product.data.sale_price ?? price
  }

  const selected = getVariations(product).find((v) => v.id.text === variationId)
  if (selected) {
    price = selected.sale_price_variation ?? selected.price
  }

  return price
}

const FORM_URL = 'bestellformular'

export default ({ section }) => {
  const { lang } = useLang()
  const { cart, isInCart, removeFromCart } = useCart()
  const { isInFavs, removeFromFavs } = useFavs()

  const formVisible = section === FORM_URL

  const {
    products_button_text,
    header_link_cart,
    cart_empty_text,
    cart_back_button,
    cart_order_button,

    product_preis_label,

    form_title,
    form_label_anrede,
    form_anrede_options,
    form_label_first_name,
    form_label_last_name,
    form_label_street,
    form_label_country,
    form_country_options,
    form_label_plz,
    form_label_city,
    form_label_phone,
    form_label_email,
    form_label_notes,
    form_label_newsletter,
    form_newsletter_text,
    form_button,
  } = useTexts()

  const cartLink = useInternalLink(`einkaufsliste/${FORM_URL}`)
  const thanksLink = useInternalLink('danke')

  return (
    <Layout>
      <SEO
        title={header_link_cart}
        url="https://klingenland.at/einkaufsliste"
      />

      <div className="dd-container">
        <div className="dd-title flex items-center justify-between">
          <h1 className="dd-h2">{header_link_cart}</h1>
          <Cart className="text-white w-10 h-10" />
        </div>

        {'undefined' !== typeof localStorage && cart.length === 0 && (
          <div className="mt-16 flex flex-col items-center">
            <p className="max-w-lg text-center">{cart_empty_text}</p>
            <Link to={useInternalLink('produkte')} className="dd-b-alt mt-8">
              {products_button_text}
            </Link>
          </div>
        )}

        {'undefined' !== typeof localStorage && cart.length > 0 && (
          <div>
            <ul className="mt-8">
              {cart.map((item) => (
                <Row
                  key={item.uid}
                  uid={item.uid}
                  product={item.product}
                  amount={item.amount}
                  onRemove={removeFromCart}
                />
              ))}
            </ul>

            <div className="mb-16 flex justify-end">
              <div className="flex items-baseline justify-end border-t-2 border-gray-100 h-12 py-4 w-48">
                <p className="dd-label mr-4">{product_preis_label}</p>
                <p className="text-2xl">
                  {cart.reduce((sum, item) => {
                    // remove legacy items
                    if (!item.product.data && item.product._meta) {
                      if (isInCart(item.product._meta.uid))
                        removeFromCart(item.product._meta.uid)
                      if (isInFavs(item.product._meta.uid))
                        removeFromFavs(item.product._meta.uid)
                      return 0
                    }

                    const [uid, variationId] =
                      item.uid.split(VARIATION_SEPARATOR)

                    return (
                      sum + getPrice(item.product, variationId) * item.amount
                    )
                  }, 0)}{' '}
                  €
                </p>
              </div>
            </div>

            <div className="flex items-stretch justify-between flex-col sm:flex-row">
              {!formVisible && (
                <Link to={useInternalLink('produkte')}>
                  <button className="h-full dd-b-alt">
                    {cart_back_button}
                  </button>
                </Link>
              )}
              {!formVisible && (
                <button
                  className="dd-b mt-12 sm:mt-0 self-end"
                  onClick={() => {
                    navigate(cartLink)
                  }}
                >
                  {cart_order_button}
                </button>
              )}
            </div>

            {formVisible && (
              <Form
                url="order"
                onSubmit={(response) => {
                  if (1 !== response.code) return

                  cart.forEach((item) => {
                    removeFromCart(item.uid)
                  })

                  navigate(thanksLink)
                }}
              >
                {(register, state) => (
                  <fieldset
                    className="relative m-0 p-0"
                    disabled={state.loading}
                  >
                    {1 === state.response.code ? (
                      ''
                    ) : (
                      <div className="px-1 flex flex-col">
                        <h2 className="px-2 mb-2 dd-hx dd-font text-xl">
                          {form_title}
                        </h2>
                        <input
                          type="hidden"
                          defaultValue={lang}
                          {...register('lang')}
                        />
                        <input
                          type="hidden"
                          defaultValue={JSON.stringify(
                            cart.map((p) => {
                              const [uid, variationId] =
                                p.uid.split(VARIATION_SEPARATOR)

                              return {
                                uid,
                                amount: p.amount,
                                price: getPrice(p.product, variationId),
                                type: p.product.type,
                                variation: variationId,
                              }
                            })
                          )}
                          {...register('cart')}
                        />
                        <div className="sm:mt-4 flex flex-col sm:flex-row">
                          <label className="relative mt-4 sm:mt-0 block px-1 sm:w-28">
                            <p className="ml-1 dd-label">
                              {form_label_anrede}
                              <RequiredAsterisk />
                            </p>
                            <select
                              className="dd-input mt-1"
                              {...register('prefix')}
                            >
                              {form_anrede_options.map((o, i) => (
                                <option key={i} className="">
                                  {o}
                                </option>
                              ))}
                            </select>
                            <Arrow
                              className="absolute pointer-events-none top-0 mt-10 right-0 mr-5 w-3 h-3"
                              style={{
                                fill: tailwind.theme.colors.gray[800],
                                transform: 'rotate(90deg)',
                              }}
                            />
                          </label>
                          <Input
                            required
                            label={form_label_first_name}
                            name="firstName"
                            register={register}
                          />
                          <Input
                            required
                            label={form_label_last_name}
                            name="lastName"
                            register={register}
                          />
                        </div>
                        <div className="sm:mt-4 flex flex-col sm:flex-row">
                          <Input
                            required
                            label={form_label_street}
                            name="street"
                            register={register}
                          />
                          <label className="relative mt-4 sm:mt-0 block px-1 sm:w-48">
                            <p className="ml-1 dd-label">
                              {form_label_country}
                              <RequiredAsterisk />
                            </p>
                            <select
                              className="dd-input mt-1"
                              {...register('country')}
                            >
                              {form_country_options.map((o, i) => (
                                <option key={i} className="">
                                  {o}
                                </option>
                              ))}
                            </select>
                            <Arrow
                              className="absolute pointer-events-none top-0 mt-10 right-0 mr-5 w-3 h-3"
                              style={{
                                fill: tailwind.theme.colors.gray[800],
                                transform: 'rotate(90deg)',
                              }}
                            />
                          </label>
                        </div>
                        <div className="sm:mt-4 flex flex-col sm:flex-row">
                          <Input
                            className="sm:w-28 flex-initial"
                            required
                            label={form_label_plz}
                            name="zip"
                            register={register}
                          />
                          <Input
                            required
                            label={form_label_city}
                            name="city"
                            register={register}
                          />
                        </div>
                        <div className="sm:mt-4 flex flex-col sm:flex-row">
                          <Input
                            required
                            label={form_label_phone}
                            name="phone"
                            type="tel"
                            register={register}
                          />
                        </div>
                        <div className="sm:mt-4 flex flex-col sm:flex-row">
                          <Input
                            required
                            label={form_label_email}
                            name="email"
                            type="email"
                            register={register}
                          />
                        </div>
                        <div className="sm:mt-4 flex flex-col sm:flex-row">
                          <label className="mt-4 sm:mt-0 flex-1 block px-1">
                            <p className="ml-1 dd-label">{form_label_notes}</p>
                            <textarea
                              className="mt-1 dd-input"
                              {...register('note')}
                            ></textarea>
                          </label>
                        </div>

                        <div className="px-1 mt-8">
                          <label>
                            <div className="flex items-start">
                              <input
                                type="checkbox"
                                {...register('newsletter')}
                              />
                              <p className="dd-label ml-4 leading-none">
                                {form_label_newsletter}
                              </p>
                            </div>
                            <p className="mt-2 text-xs mb-4">
                              {form_newsletter_text}
                            </p>
                          </label>
                        </div>

                        <button
                          className="ml-1 mt-4 py-3 px-5 dd-b self-end"
                          type="submit"
                        >
                          {form_button}
                        </button>
                        {state.loading && (
                          <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center">
                            <div className="bg-gray-200 opacity-50 absolute top-0 left-0 w-full h-full "></div>
                            <Spinner className="z-10 w-24 h-24" />
                          </div>
                        )}
                      </div>
                    )}
                  </fieldset>
                )}
              </Form>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
